import { useEffect } from 'react'
import { useDispatch, useSelector, batch } from 'react-redux'
import { rootActions } from '@app-store/slices'
import { setEventCookie } from '@features/Auth'
import { history } from '@components/App'
import routes from '@utils/constants/routes'
import { RootState } from '@app-store/store'
import { eventColorHelper } from '@utils/helpers/commonHelpers'
import { LOCALSTORAGE_KEYS, COOKIE_KEYS, getCookie } from '@features/General'
import { USER_ROLES, USER_TYPES } from '@utils/constants'
import Can from '@ability/index'
import { isActiveFeature } from '@features/General/handlers'
import useMixpanel from 'hooks/processor/useMixpanel'
import { useUser } from 'hooks'
import { CompanySubscriptionStatus } from '@features/Settings/Account/Subscription'

const useChangeEvent = () => {
    const dispatch = useDispatch()
    const { setMixPanelData } = useMixpanel()
    const { companyId: userCompanyId, contactId } = useUser()
    const { data: event }: any = useSelector((state: RootState) => state.events.oneEvent)
    const { subscriptionId, isFree } = useSelector((state: RootState) => state.ui.subscription.data)
    const { isMeetingHubEvent } = useSelector((state: RootState) => state.ui.companyProfile)

    useEffect(() => {
        if (event?._id) {
            eventColorHelper({
                branding: event?.brandings?.length ? event.brandings[event.brandings.length - 1] : event?.branding
            })
        }
    }, [event])

    const redirectToOnboarding = (data, eventId) => {
        const user: any = JSON.parse(localStorage.getItem(LOCALSTORAGE_KEYS.USERINFO))
        const eventUserInfo = user?.eventInfos?.find(x => x?.eventId === eventId)
        if (data.isCompanyFirstTime || eventUserInfo?.role?.title === 'Admin') {
            dispatch(
                rootActions.companies.onboardingExhibitor.set({
                    type: 'isAdmin',
                    value: true
                })
            )
        }
        switch (true) {
            case !!subscriptionId && !isFree:
                break
            case [USER_TYPES.ADMIN, USER_TYPES.ORGANIZER].includes(eventUserInfo?.role?.type):
                history.push(routes.organizer._)
                break
            case data.isCompanyFirstTime && data.isUserFirstTime:
                history.push(routes.exhibitor.onboarding.itp._.replace(':state', 'location'))
                break
            case data.isUserFirstTime:
                history.push(routes.exhibitor.onboarding.profile._)
                break
            case !data.itpOnboarded && (eventUserInfo?.role?.title === 'Admin' || eventUserInfo?.role?.type === 'User'):
                history.push(routes.exhibitor.onboarding.itp._.replace(':state', 'location'))
                break
            case data.itpOnboarded && !data.companyOnboarded && eventUserInfo?.role?.title === 'Admin':
                history.push(routes.exhibitor.onboarding.profile._)
                break
            case data.itpOnboarded && !data.contactOnboarded && eventUserInfo?.role?.title !== 'Admin':
                history.push(routes.exhibitor.onboarding.profile._)
                break
            default:
                history.push(routes.organizer._)
                break
        }
    }

    const callChangeEventEndpoints = data => {
        const {
            eventId,
            eventChanged = true,
            redirectRoute = '',
            isPaymentLogin = false,
            onError = () => undefined,
            onSuccess = () => undefined
        } = data
        setEventCookie(eventId)
        const isViewer = getCookie(COOKIE_KEYS.ALA_USER_ROLE) === USER_ROLES.VIEWER
        dispatch(rootActions.eventSetting.oneSetting.onResetData())
        dispatch(rootActions.eventSetting.oneSectionSetting.onResetData())
        batch(() => {
            const callEndpoints = (onboardingData = {}) => {
                dispatch(
                    rootActions.users.profile.onRequest({
                        sagaCB: {
                            onSuccess: () => {
                                dispatch(
                                    rootActions.companies.contactProfile.onRequest({
                                        sagaCB: {
                                            onSuccess: contactData => {
                                                dispatch(rootActions.localization.languageTypes.onRequest())
                                                dispatch(rootActions.events.checkEventPublishability.onRequest({}))
                                                dispatch(
                                                    rootActions.eventSetting.oneSectionSetting.onRequest({
                                                        sectionName: 'menuVisibility',
                                                        settingId: 'default'
                                                    })
                                                )
                                                dispatch(
                                                    rootActions.eventSetting.activeFeatures.onRequest({
                                                        sagaCB: {
                                                            onSuccess: () => {
                                                                if (eventChanged || isPaymentLogin) {
                                                                    dispatch(
                                                                        rootActions.meetingRequests.list.onRequest({
                                                                            query: { hostIds: { $in: [contactId] } },
                                                                            limit: 1,
                                                                            noSaveInStore: true,
                                                                            hasTotal: true,
                                                                            sagaCB: {
                                                                                onSuccess: outgoingRequests => {
                                                                                    dispatch(
                                                                                        rootActions.meetingRequests.list.onRequest(
                                                                                            {
                                                                                                query: {
                                                                                                    guestIds: {
                                                                                                        $in: [contactId]
                                                                                                    }
                                                                                                },
                                                                                                limit: 1,
                                                                                                noSaveInStore: true,
                                                                                                hasTotal: true,
                                                                                                sagaCB: {
                                                                                                    onSuccess:
                                                                                                        incomingRequests => {
                                                                                                            if (
                                                                                                                isMeetingHubEvent
                                                                                                            ) {
                                                                                                                dispatch(
                                                                                                                    rootActions.companySubscriptions.list.onRequest(
                                                                                                                        {
                                                                                                                            query: {
                                                                                                                                company:
                                                                                                                                    userCompanyId,
                                                                                                                                status: {
                                                                                                                                    $in: [
                                                                                                                                        CompanySubscriptionStatus.ACTIVE,
                                                                                                                                        CompanySubscriptionStatus.PAUSE
                                                                                                                                    ]
                                                                                                                                }
                                                                                                                            },
                                                                                                                            needPopulate:
                                                                                                                                true,
                                                                                                                            populates:
                                                                                                                                [
                                                                                                                                    'company',
                                                                                                                                    'subscription',
                                                                                                                                    'users',
                                                                                                                                    'payment'
                                                                                                                                ],
                                                                                                                            sort: '-createdAt',
                                                                                                                            limit: 1,
                                                                                                                            sagaCB: {
                                                                                                                                onSuccess:
                                                                                                                                    response => {
                                                                                                                                        setMixPanelData(
                                                                                                                                            contactData,
                                                                                                                                            onboardingData,
                                                                                                                                            response
                                                                                                                                                ?.data?.[0],
                                                                                                                                            outgoingRequests?.total,
                                                                                                                                            incomingRequests?.total
                                                                                                                                        )
                                                                                                                                    }
                                                                                                                            }
                                                                                                                        }
                                                                                                                    )
                                                                                                                )
                                                                                                            } else {
                                                                                                                setMixPanelData(
                                                                                                                    contactData,
                                                                                                                    onboardingData,
                                                                                                                    {},
                                                                                                                    outgoingRequests?.total,
                                                                                                                    incomingRequests?.total
                                                                                                                )
                                                                                                            }
                                                                                                        }
                                                                                                }
                                                                                            }
                                                                                        )
                                                                                    )
                                                                                }
                                                                            }
                                                                        })
                                                                    )

                                                                    if (redirectRoute) {
                                                                        history.push(redirectRoute)
                                                                    } else if (!isPaymentLogin) {
                                                                        redirectToOnboarding(onboardingData, eventId)
                                                                    }
                                                                } else if (!isPaymentLogin) {
                                                                    if (isViewer) {
                                                                        history.push(routes.analytics._)
                                                                    } else if (redirectRoute) {
                                                                        history.push(redirectRoute)
                                                                    }
                                                                }
                                                                if (
                                                                    isActiveFeature('Chat') &&
                                                                    Can(['Login', 'users'])
                                                                ) {
                                                                    dispatch(
                                                                        rootActions.chatDetails.login.onRequest({})
                                                                    )
                                                                }
                                                                onSuccess()
                                                            },
                                                            onError: () => {
                                                                if (eventChanged || isPaymentLogin) onError()
                                                            }
                                                        }
                                                    })
                                                )
                                                dispatch(rootActions.eventSetting.eventProfiles.onRequest())
                                                dispatch(rootActions.notification.getUnreadNumbers.onRequest())
                                            },
                                            onError: result => {
                                                if (result?.message === 'Un-authorized Forbidden') {
                                                    history.push(routes.login._)
                                                }
                                            }
                                        }
                                    })
                                )
                            }
                        }
                    })
                )
                dispatch(rootActions.events.oneEvent.onRequest({ id: eventId }))
            }
            if (eventChanged || isPaymentLogin) {
                dispatch(
                    rootActions.companies.checkStatusEvent.onRequest({
                        event: { _id: eventId },
                        sagaCB: {
                            onSuccess: onboardingData => {
                                callEndpoints(onboardingData)
                            },
                            onError
                        }
                    })
                )
            } else {
                callEndpoints()
            }
        })
    }

    return {
        callChangeEventEndpoints,
        redirectToOnboarding
    }
}

export default useChangeEvent
